import { useEffect, useState, useCallback } from "react";
import DashboardShell from "@/components/DashboardShell";
import api, { formatApiError, downloadInvoicePdf, downloadPdf, fileUrl } from "@/lib/api";
import OrderMiniMap from "@/components/OrderMiniMap";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  LayoutDashboard, Package, Users, Tag, Truck, FileText, CreditCard, Settings as SettingsIcon,
  Plus, Trash2, Check, AlertTriangle, Download, Pencil, Repeat, Pause, Play, Mail, MessageSquare, QrCode, Scale, History, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const money = (n) => "£" + Number(n || 0).toFixed(2);
const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "orders", label: "Orders", icon: Package },
  { key: "recurring", label: "Recurring", icon: Repeat },
  { key: "clients", label: "Clients", icon: Users },
  { key: "pricing", label: "Price list", icon: Tag },
  { key: "drivers", label: "Drivers & jobs", icon: Truck },
  { key: "invoices", label: "Invoices", icon: FileText },
  { key: "subscription", label: "Subscription", icon: CreditCard },
  { key: "account", label: "Settings", icon: SettingsIcon },
];
const STATUS_COLORS = {
  new: "bg-slate-100 text-slate-700", collected: "bg-blue-100 text-blue-700",
  processing: "bg-amber-100 text-amber-700", ready: "bg-teal-100 text-teal-700",
  out_for_delivery: "bg-indigo-100 text-indigo-700", completed: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};


function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [clients, setClients] = useState([]);
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_id: "", order_ids: [], tax_rate: 0, due_date: "", notes: "" });
  const load = useCallback(() => {
    api.get("/business/invoices").then((r) => { setInvoices(r.data); setLoaded(true); });
    api.get("/business/clients").then((r) => setClients(r.data));
    api.get("/business/orders").then((r) => setOrders(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const clientOrders = orders.filter((o) => o.client_id === form.client_id && !o.invoice_id);
  const toggleOrder = (id) => setForm((f) => ({ ...f, order_ids: f.order_ids.includes(id) ? f.order_ids.filter((x) => x !== id) : [...f.order_ids, id] }));

  const create = async () => {
    if (!form.client_id) return toast.error("Choose a client");
    if (!form.order_ids.length) return toast.error("Select at least one order");
    try { await api.post("/business/invoices", { ...form, tax_rate: Number(form.tax_rate) || 0 });
      toast.success("Invoice created"); setOpen(false); setForm({ client_id: "", order_ids: [], tax_rate: 0, due_date: "", notes: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setStatus = async (id, status) => { await api.put(`/business/invoices/${id}/status`, { status }); load(); };
  const remove = async (id) => { if (!window.confirm("Delete this invoice?")) return; await api.delete(`/business/invoices/${id}`); load(); };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold text-slate-900">Invoices</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full bg-brand hover:bg-brand-hover" data-testid="new-invoice-btn"><Plus className="mr-1 h-4 w-4" /> New invoice</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New invoice</DialogTitle><DialogDescription>Generate an invoice from a client&rsquo;s unbilled orders.</DialogDescription></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Client</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm((f) => ({ ...f, client_id: v, order_ids: [] }))}>
                  <SelectTrigger data-testid="invoice-client-select"><SelectValue placeholder="Select a client" /></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.client_id && (
                <div>
                  <Label>Unbilled orders</Label>
                  <div className="mt-1.5 max-h-48 space-y-2 overflow-auto">
                    {clientOrders.map((o) => (
                      <label key={o.id} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm" data-testid={`invoice-order-${o.ref}`}>
                        <span className="flex items-center gap-2"><input type="checkbox" checked={form.order_ids.includes(o.id)} onChange={() => toggleOrder(o.id)} /> #{o.ref} · {o.created_at?.slice(0,10)}</span>
                        <span className="font-medium">{money(o.total)}</span>
                      </label>
                    ))}
                    {!clientOrders.length && <p className="text-xs text-slate-400">No unbilled orders for this client.</p>}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Tax rate (%)</Label><Input type="number" value={form.tax_rate} onChange={(e) => setForm((f) => ({ ...f, tax_rate: e.target.value }))} data-testid="invoice-tax" /></div>
                <div className="space-y-1.5"><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} data-testid="invoice-due" /></div>
              </div>
            </div>
            <DialogFooter><Button onClick={create} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="invoice-create-btn">Create invoice</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="invoices-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400"><tr><th className="px-4 py-3">Number</th><th className="px-4 py-3">Client</th><th className="px-4 py-3">Total</th><th className="px-4 py-3">Due</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Actions</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {invoices.map((inv) => (
              <tr key={inv.id} data-testid={`invoice-row-${inv.number}`}>
                <td className="px-4 py-3 font-mono text-xs">{inv.number}</td>
                <td className="px-4 py-3">{inv.client_name}</td>
                <td className="px-4 py-3 font-medium">{money(inv.total)}</td>
                <td className="px-4 py-3 text-slate-500">{inv.due_date || "—"}</td>
                <td className="px-4 py-3"><Badge className={`border-0 ${inv.status === "paid" ? "bg-green-100 text-green-700" : inv.status === "sent" ? "bg-blue-100 text-blue-700" : inv.status === "void" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"}`}>{inv.status}</Badge></td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    {inv.status === "draft" && <Button size="sm" variant="outline" onClick={() => setStatus(inv.id, "sent")} data-testid={`invoice-send-${inv.number}`}>Send</Button>}
                    {inv.status !== "paid" && inv.status !== "void" && <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => setStatus(inv.id, "paid")} data-testid={`invoice-paid-${inv.number}`}><Check className="mr-1 h-3 w-3" /> Mark paid</Button>}
                    <Button size="sm" variant="outline" onClick={() => downloadInvoicePdf(inv.id, inv.number).catch(() => toast.error("PDF failed"))} data-testid={`invoice-pdf-${inv.number}`}><Download className="mr-1 h-3 w-3" /> PDF</Button>
                    <button onClick={() => remove(inv.id)} className="text-slate-300 hover:text-red-500" data-testid={`invoice-delete-${inv.number}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!invoices.length && <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-400">{loaded ? "No invoices yet." : "Loading…"}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ------------------------------ Subscription ------------------------------ */

export default Invoices;
